// @ts-nocheck
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import type { ChartData, ChartOptions } from 'chart.js';
import type { ColDef, GridApi, GridReadyEvent, CellValueChangedEvent } from 'ag-grid-community';
import * as XLSX from 'xlsx';
import {
  NJButton,
  NJCheckbox,
  NJFormItem,
  NJIcon,
  NJIconButton,
  NJInlineMessage,
  NJSegmentedControl,
  NJSegmentedControlButton,
  NJSlider,
  NJSpinner,
  NJTab,
  NJTabPanel,
  NJTabs,
  NJText,
  NJToggle,
  NJToastContainer,
  NJToastService,
  NJTooltip,
} from '@engie-group/fluid-design-system-react';
import PrototypeStudioLayout from '@shared/PrototypeStudioLayout';
import { MixedChart } from '@shared';
import DataTable, { getCellClassName } from '@shared/DataTable';
import { usePlannings } from './hooks';
import type { AdvancedOptions, DemandTab, FoisonnementKind, RegulationMode, TabId, TempDemandRow, VizMode } from './types';

export const meta = {
  title: 'Formulaire création optimisation atemporelle',
  description:
    "Formulaire multi-onglets pour configurer une optimisation paramétrique atemporelle (eau chaude) : général, planning, options avancées.",
  tags: ['form', 'timeless', 'optimization', 'wizard', 'hot-water'],
  author: 'pm-team',
  status: 'ready' as const,
};

// ─── Constants (matching real app values) ────────────────────────────────────

const DELTA_TEMPERATURE = 'delta_t';
const OUTPUT_TEMPERATURE = 'output_temperature';

const TABS: { id: TabId; label: string; shortLabel: string }[] = [
  { id: 'general',              label: '1. Général',                  shortLabel: '1' },
  { id: 'temperatures-demand',  label: '2. Températures et demande',  shortLabel: '2' },
  { id: 'planning',             label: '3. Planning',                 shortLabel: '3' },
  { id: 'advanced',             label: '4. Options avancées',         shortLabel: '4' },
];

function useIsSmallScreen() {
  const [isSmall, setIsSmall] = useState(() => window.matchMedia('(max-width: 900px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 900px)');
    const handler = (e: MediaQueryListEvent) => setIsSmall(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isSmall;
}

const DEFAULT_ADVANCED_OPTIONS: AdvancedOptions = {
  pipes_sizing: true,
  diameter_homogenization: true,
  report_power_reserve: true,
  generate_influence_zones_files: true,
  generate_limiting_factors_report: true,
  report_critical_path: true,
  iis: true,
  reduce_network: false,
  delta_t_min: false,
  ets_flow_correction: false,
  total_production_correction: false,
  short: false,
  debug_generate_logs: false,
  generate_support_reports: false,
  test_feasopt: false,
  test_relax_uc: false,
  relax_regulators: false,
  two_stage_solve_lt: false,
};

const SP24 = 'var(--nj-semantic-size-spacing-24)';
const SP16 = 'var(--nj-semantic-size-spacing-16)';
const SP8  = 'var(--nj-semantic-size-spacing-8)';

// ─── Shared helpers ───────────────────────────────────────────────────────────

interface ToggleRowProps {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}
function ToggleRow({ label, checked, onChange }: ToggleRowProps) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: SP8, paddingBlock: '6px' }}>
      <NJToggle
        label=""
        checked={checked}
        onChange={() => onChange(!checked)}
      />
      <NJText scale="sm" style={{ margin: 0 }}>{label}</NJText>
    </div>
  );
}

interface ToggleSectionProps { title: string; children: React.ReactNode; }
function ToggleSection({ title, children }: ToggleSectionProps) {
  return (
    <div style={{ marginBottom: SP24 }}>
      <p style={{
        margin: `0 0 ${SP8} 0`,
        fontSize: '16px',
        fontWeight: 'var(--nj-core-font-weight-semibold)',
        textTransform: 'capitalize',
      }}>
        {title}
      </p>
      {children}
    </div>
  );
}

// ─── Tab 1: Général ───────────────────────────────────────────────────────────

interface GeneralTabProps {
  nom: string; onNomChange: (v: string) => void;
  description: string; onDescriptionChange: (v: string) => void;
  tempMin: number; onTempMinChange: (v: number) => void;
  tempMax: number; onTempMaxChange: (v: number) => void;
  nombreDePas: number; onNombreDePasChange: (v: number) => void;
  regulationMode: RegulationMode; onRegulationModeChange: (v: RegulationMode) => void;
  advancedOptions: AdvancedOptions;
  onToggleOption: (key: keyof AdvancedOptions, value: boolean) => void;
  isActive: boolean;
}
function GeneralTab({
  nom, onNomChange,
  description, onDescriptionChange,
  tempMin, onTempMinChange,
  tempMax, onTempMaxChange,
  nombreDePas, onNombreDePasChange,
  regulationMode, onRegulationModeChange,
  advancedOptions, onToggleOption,
  isActive,
}: GeneralTabProps) {
  const [sliderKey, setSliderKey] = useState(0);
  const isSmall = useIsSmallScreen();

  // rawNbSteps holds the in-progress string value of the number input so the user
  // can type an out-of-range value and see an error before correcting it.
  const [rawNbSteps, setRawNbSteps] = useState(nombreDePas.toString());

  // Keep rawNbSteps in sync when the slider moves (nombreDePas changes from drag).
  useEffect(() => { setRawNbSteps(nombreDePas.toString()); }, [nombreDePas]);

  const nbStepsErrorMsg = (() => {
    const v = Number.parseInt(rawNbSteps, 10);
    if (Number.isNaN(v)) return 'Veuillez saisir un nombre entier';
    if (v > 40) return 'La valeur maximale est 40';
    if (v < 1) return 'La valeur minimale est 1';
    return '';
  })();

  const isTempRangeInvalid = nombreDePas !== 1 && Number(tempMin) >= Number(tempMax);

  return (
    <div className="general-layout" style={{
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'stretch',
      minHeight: '600px',
      overflowY: 'auto',
      background: '#ffffff',
    }}>

      {/* ── Left column: Long description ── */}
      <div className="general-left" style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: SP24 }}>
        <NJText style={{ fontFamily: "'Lato', sans-serif", display: 'block', marginBottom: SP16 }}>
          <strong>Optimisation atemporelle</strong>
        </NJText>
        <div style={{ textAlign: 'justify', flex: 1, display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <NJText scale="sm">
            {"L'"}
            <strong style={{ color: '#CD0003' }}>
              optimisation atemporelle
            </strong>
            {" est un calcul visant à minimiser les coûts opérationnels (OPEX) du réseau de chaleur."}
          </NJText>
          <NJText scale="sm">
            Elle explore une plage de températures extérieures et, pour chaque valeur, détermine
            le mix de production optimal (chaudières, PAC, appoint) permettant de satisfaire la
            demande thermique au moindre coût.
          </NJText>
          <NJText scale="sm">
            Les résultats se présentent sous forme d&apos;une courbe de production en fonction de la
            température, exploitable dans les études de dimensionnement et de planification des
            investissements.
          </NJText>
          <NJText scale="sm">
            Contrairement à l&apos;optimisation trajectoire, elle n&apos;utilise pas de série temporelle
            mais des profils agrégés représentatifs de la demande — les variations saisonnières
            ne sont pas modélisées.
          </NJText>
        </div>
      </div>

      {/* ── Separator 1 ── */}
      <div className="general-separator" style={{ width: '1px', background: 'var(--nj-core-color-grey-300)', margin: '0 var(--nj-semantic-size-spacing-16)' }} />

      {/* ── Middle column: Informations générales ── */}
      <div className="general-middle" style={{ flex: 1.8, padding: SP24 }}>
        <NJText style={{ fontFamily: "'Lato', sans-serif", display: 'block' }}>
          <strong>Informations générales</strong>
        </NJText>

        <div style={{ marginTop: SP16 }}>
          <NJFormItem
            labelKind="static"
            value={nom}
            id="label"
            label={isSmall ? 'Nom *' : 'Nom (Obligatoire)'}
            onChange={(e) => onNomChange((e.target as HTMLInputElement).value)}
          />
        </div>

        <div style={{ marginTop: SP16 }}>
          <NJFormItem
            labelKind="static"
            value={description}
            id="description"
            label="Description"
            isMultiline
            maxLength={200}
            onChange={(e) => onDescriptionChange((e.target as HTMLInputElement).value)}
          />
        </div>

        <div style={{
          marginTop: SP16,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
        }}>
          <div style={{ flexShrink: 0, paddingRight: SP16 }}>
            <svg width="64" height="64" viewBox="0 0 70 70" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path fillRule="evenodd" clipRule="evenodd" d="M38.9756 0.000537301C41.0338 -0.0234028 43.0209 0.753486 44.5176 2.16655C46.0141 3.5796 46.9038 5.51871 46.998 7.57476V15.4009C46.9748 15.6069 46.8744 15.7964 46.7178 15.9322C46.5624 16.0671 46.363 16.1421 46.1572 16.1421C46.0955 16.1421 46.035 16.1328 45.9756 16.1197V18.6402C46.0522 18.6175 46.1325 18.606 46.2139 18.606C46.4365 18.6061 46.6502 18.6948 46.8076 18.8521C46.9651 19.0096 47.0537 19.224 47.0537 19.4468L46.9834 42.1685C47.7373 42.6643 48.4491 43.2217 49.1113 43.8345L50.1758 44.8003C51.8824 46.6737 53.0904 48.9464 53.6895 51.4087C54.2944 53.8986 54.2465 56.5025 53.5498 58.9683C52.7738 61.7028 51.2414 64.1631 49.1299 66.066C47.0185 67.9686 44.4132 69.2367 41.6133 69.7251C38.8131 70.2135 35.9318 69.9024 33.3008 68.8267C30.6698 67.751 28.3952 65.9544 26.7393 63.6441C25.3418 61.7094 24.4301 59.4668 24.0801 57.106C23.7268 54.758 23.9326 52.3593 24.6816 50.106C25.7818 46.8506 27.9862 44.0827 30.9121 42.2808L31.0234 42.1822V7.61675C31.1057 5.5681 31.9794 3.6308 33.46 2.21245C34.946 0.793776 36.9211 0.00163747 38.9756 0.000537301ZM25.2979 27.4126C25.4342 27.4553 25.5624 27.521 25.6758 27.608C25.7814 27.6849 25.8678 27.7857 25.9277 27.9019C25.9835 28.0101 26.0123 28.1308 26.0117 28.2525C26.0106 28.3695 25.9818 28.4847 25.9277 28.5884C25.8678 28.7046 25.7814 28.8054 25.6758 28.8824C25.5624 28.9693 25.4343 29.036 25.2979 29.0787C25.191 29.0923 25.0824 29.0923 24.9756 29.0787H16.127C15.9905 29.036 15.8625 28.9693 15.749 28.8824C15.6434 28.8054 15.557 28.7046 15.4971 28.5884C15.443 28.4847 15.4142 28.3695 15.4131 28.2525C15.4125 28.1308 15.4413 28.0101 15.4971 27.9019C15.557 27.7857 15.6434 27.6849 15.749 27.608C15.8624 27.521 15.9906 27.4553 16.127 27.4126H24.9756C25.0824 27.399 25.191 27.399 25.2979 27.4126ZM20.7051 7.04155C20.9784 7.04155 21.2425 7.14171 21.4473 7.3228C21.5391 7.40614 21.6135 7.50742 21.6641 7.62065C21.7145 7.73374 21.7404 7.8562 21.7412 7.98003V10.8921H24.9756C25.0777 10.8788 25.1811 10.8788 25.2832 10.8921C25.4327 10.9683 25.5664 11.0735 25.6758 11.2007C25.7777 11.2924 25.8633 11.401 25.9277 11.522C25.9828 11.6403 26.0107 11.7695 26.0107 11.9C26.0107 12.0304 25.9827 12.1596 25.9277 12.2779C25.8632 12.399 25.7778 12.5084 25.6758 12.6001C25.5573 12.6874 25.4241 12.754 25.2832 12.7964C25.1811 12.8097 25.0777 12.8098 24.9756 12.7964H21.7412V15.6802C21.7404 15.8043 21.7147 15.9273 21.6641 16.0406C21.6135 16.1538 21.5391 16.2551 21.4473 16.3384C21.2619 16.5058 21.0247 16.605 20.7754 16.6187C20.5018 16.6216 20.2367 16.5214 20.0332 16.3384C19.8528 16.1661 19.7472 15.9295 19.7393 15.6802V12.7681H16.4355C16.3334 12.7814 16.23 12.7814 16.1279 12.7681C15.9924 12.7371 15.8639 12.6799 15.75 12.6001C15.6399 12.5161 15.5522 12.405 15.4971 12.2779C15.4335 12.1622 15.4004 12.0319 15.4004 11.9C15.4005 11.7679 15.4334 11.6377 15.4971 11.522C15.5522 11.395 15.6399 11.2848 15.75 11.2007C15.8617 11.111 15.9902 11.044 16.1279 11.0044C16.23 10.9911 16.3334 10.9911 16.4355 11.0044H19.6699V7.98003C19.678 7.73079 19.7835 7.49505 19.9639 7.3228C20.1685 7.14181 20.4319 7.04166 20.7051 7.04155Z" fill="white"/>
              <path d="M40.1928 63.5189C39.6749 63.5191 39.1585 63.4628 38.6528 63.3509C37.4481 63.02 36.3308 62.4284 35.3801 61.618C34.4293 60.8075 33.6683 59.798 33.1508 58.6609C32.5854 57.5704 32.2695 56.3679 32.2259 55.1403C32.1823 53.9127 32.4122 52.6908 32.8988 51.5629C33.7139 50.322 34.9217 49.3904 36.3288 48.9169L37.2248 48.4829L37.3508 21.2669C37.3225 20.8608 37.3788 20.4533 37.5161 20.0701C37.6535 19.687 37.8689 19.3365 38.1488 19.0409C38.4208 18.7393 38.7552 18.5004 39.1288 18.3409C39.5034 18.1745 39.9088 18.0886 40.3188 18.0889C40.7244 18.0867 41.1257 18.1727 41.4948 18.3409C41.8734 18.4986 42.2127 18.7376 42.4888 19.0409C42.7662 19.3351 42.9806 19.6829 43.1188 20.0629C43.2463 20.4508 43.2986 20.8594 43.2728 21.2669V48.3989C44.4654 49.0378 45.4763 49.9689 46.2108 51.1051C46.9454 52.2412 47.3796 53.5452 47.4728 54.8949C47.5694 56.0359 47.4317 57.1845 47.0681 58.2703C46.7046 59.3561 46.1229 60.3561 45.3588 61.2089C44.6904 61.9348 43.8804 62.5161 42.9788 62.9169C42.0959 63.3054 41.1433 63.5102 40.1788 63.5189H40.1928Z" fill="#7EAEE9"/>
              <path d="M38.9749 63.001C38.4099 63.0028 37.8464 62.9418 37.2949 62.819C36.1123 62.5595 35.005 62.032 34.0584 61.2772C33.1118 60.5223 32.3511 59.5602 31.8349 58.465C31.3116 57.3679 31.0436 56.1665 31.0509 54.951C31.0777 53.7143 31.3942 52.5012 31.9749 51.409C32.8152 49.7944 34.177 48.5115 35.8389 47.769V27.077C35.811 26.962 35.811 26.842 35.8389 26.727C35.875 26.6104 35.9319 26.5013 36.0069 26.405C36.0929 26.3196 36.1922 26.2486 36.3009 26.195C36.4118 26.1488 36.5308 26.125 36.6509 26.125C36.7711 26.125 36.89 26.1488 37.0009 26.195C37.1096 26.2486 37.2089 26.3196 37.2949 26.405C37.3749 26.498 37.4324 26.6082 37.4629 26.727C37.4976 26.8411 37.4976 26.9629 37.4629 27.077V48.301C37.4631 48.4763 37.4093 48.6473 37.3089 48.791C37.2102 48.9346 37.0678 49.0426 36.9029 49.099C36.1277 49.3721 35.4141 49.7955 34.8029 50.345C34.2098 50.9035 33.7338 51.5745 33.4029 52.319C32.9964 53.1617 32.7858 54.0854 32.7869 55.021C32.7695 55.9597 32.9708 56.8896 33.3749 57.737C33.7746 58.5869 34.3695 59.3305 35.1109 59.907C35.8498 60.4926 36.7111 60.9042 37.6309 61.111C38.0906 61.212 38.5604 61.259 39.0309 61.251C40.4717 61.2448 41.8681 60.7514 42.9929 59.851C43.6302 59.336 44.1589 58.6995 44.5482 57.9785C44.9375 57.2575 45.1798 56.4664 45.2609 55.651C45.399 54.2754 45.0858 52.8921 44.3685 51.7102C43.6513 50.5282 42.5689 49.6117 41.2849 49.099C41.0897 49.0427 40.9209 48.9186 40.8089 48.749C40.6868 48.5829 40.6229 48.3811 40.6269 48.175V8.13501C40.6269 7.7637 40.4794 7.40761 40.2169 7.14506C39.9543 6.88251 39.5982 6.73501 39.2269 6.73501C39.0157 6.72562 38.8047 6.75867 38.6065 6.83221C38.4083 6.90575 38.2268 7.01828 38.0728 7.16315C37.9188 7.30802 37.7954 7.48229 37.7098 7.67567C37.6243 7.86905 37.5785 8.0776 37.5749 8.28901V23.689C37.5431 23.8956 37.4389 24.0841 37.2809 24.221C37.1256 24.3559 36.9267 24.4302 36.7209 24.4302C36.5151 24.4302 36.3163 24.3559 36.1609 24.221C36.0072 24.0827 35.9079 23.894 35.8809 23.689V8.28901C35.8809 7.43872 36.2187 6.62326 36.8199 6.02202C37.4212 5.42078 38.2366 5.08301 39.0869 5.08301C39.9372 5.08301 40.7527 5.42078 41.3539 6.02202C41.9552 6.62326 42.2929 7.43872 42.2929 8.28901V47.601C43.5868 48.1828 44.7036 49.0968 45.5298 50.2501C46.356 51.4033 46.8622 52.7547 46.9969 54.167C47.0957 55.2641 46.967 56.3699 46.6189 57.415C46.2635 58.4537 45.6962 59.4071 44.9529 60.215C44.2075 61.0304 43.302 61.6833 42.2929 62.133C41.2819 62.5769 40.1911 62.8103 39.0869 62.819L38.9749 63.001Z" fill="#182663"/>
              <path d="M47.054 19.4465C47.054 19.2238 46.9655 19.0101 46.8079 18.8526C46.6504 18.695 46.4368 18.6065 46.214 18.6065C45.9912 18.6065 45.7775 18.695 45.62 18.8526C45.4625 19.0101 45.374 19.2238 45.374 19.4465V42.6165C45.3858 42.7856 45.4494 42.9469 45.556 43.0785C45.6525 43.2185 45.7891 43.3259 45.948 43.3865C47.9419 44.5679 49.6 46.2404 50.764 48.2445C51.6215 49.7745 52.1686 51.4587 52.374 53.2005C52.6089 54.9471 52.4898 56.723 52.024 58.4225C51.0924 61.8534 48.8408 64.776 45.7611 66.552C42.6814 68.3279 39.024 68.8127 35.588 67.9005C33.8868 67.4399 32.2942 66.6457 30.9026 65.5642C29.511 64.4827 28.3483 63.1353 27.482 61.6005C25.7226 58.5448 25.2416 54.9179 26.1436 51.5092C27.0456 48.1005 29.2575 45.1861 32.298 43.4005C32.454 43.3264 32.5817 43.2035 32.662 43.0505C32.7505 42.9073 32.7989 42.7429 32.802 42.5745V7.63054C32.9082 6.04315 33.6137 4.55546 34.7756 3.46871C35.9375 2.38196 37.469 1.77737 39.06 1.77737C40.6509 1.77737 42.1824 2.38196 43.3443 3.46871C44.5062 4.55546 45.2117 6.04315 45.318 7.63054V15.4005C45.3266 15.5035 45.3558 15.6036 45.4039 15.695C45.452 15.7864 45.518 15.8672 45.598 15.9325C45.7533 16.0675 45.9522 16.1418 46.158 16.1418C46.3638 16.1418 46.5626 16.0675 46.718 15.9325C46.8748 15.7967 46.9748 15.6067 46.998 15.4005V7.57454C46.9037 5.51837 46.0145 3.5792 44.5178 2.16613C43.0212 0.753065 41.0342 -0.0234025 38.976 0.000537581C36.9214 0.00161512 34.9461 0.793744 33.46 2.21254C31.9794 3.63088 31.1063 5.56789 31.024 7.61654V42.1825L30.912 42.2805C27.9859 44.0825 25.782 46.851 24.682 50.1065C23.9329 52.3599 23.7266 54.7584 24.08 57.1065C24.43 59.4675 25.3423 61.7098 26.74 63.6445C28.3959 65.9548 30.67 67.751 33.301 68.8268C35.9321 69.9025 38.8132 70.214 41.6134 69.7256C44.4135 69.2373 47.0191 67.9688 49.1306 66.0659C51.2421 64.163 52.7739 61.703 53.55 58.9685C54.2467 56.5026 54.2949 53.8986 53.69 51.4085C53.0909 48.9462 51.8826 46.6739 50.176 44.8005L49.112 43.8345C48.4497 43.2216 47.7379 42.6644 46.984 42.1685L47.054 19.4465Z" fill="#182663"/>
              <path d="M20.7755 16.6181C20.5019 16.621 20.237 16.5211 20.0335 16.3381C19.8531 16.1657 19.7475 15.9295 19.7395 15.6801V12.7681H16.4355C16.3333 12.7814 16.2298 12.7814 16.1275 12.7681C15.9919 12.737 15.8635 12.6799 15.7495 12.6001C15.6394 12.516 15.5527 12.4051 15.4975 12.2781C15.4338 12.1623 15.4004 12.0322 15.4004 11.9001C15.4004 11.7679 15.4338 11.6379 15.4975 11.5221C15.5527 11.395 15.6394 11.2841 15.7495 11.2001C15.8612 11.1103 15.9898 11.0436 16.1275 11.0041C16.2298 10.9907 16.3333 10.9907 16.4355 11.0041H19.6695V7.98007C19.6775 7.73068 19.7831 7.4944 19.9635 7.32207C20.1683 7.14098 20.4322 7.04102 20.7055 7.04102C20.9789 7.04102 21.2428 7.14098 21.4475 7.32207C21.5394 7.40542 21.613 7.50694 21.6636 7.6202C21.7142 7.73347 21.7408 7.85601 21.7415 7.98007V10.8921H24.9755C25.0778 10.8787 25.1813 10.8787 25.2835 10.8921C25.433 10.9682 25.5662 11.0728 25.6755 11.2001C25.7777 11.2919 25.863 11.4008 25.9275 11.5221C25.9826 11.6405 26.0112 11.7695 26.0112 11.9001C26.0112 12.0307 25.9826 12.1597 25.9275 12.2781C25.863 12.3993 25.7777 12.5083 25.6755 12.6001C25.557 12.6873 25.4244 12.7536 25.2835 12.7961C25.1813 12.8094 25.0778 12.8094 24.9755 12.7961H21.7415V15.6801C21.7408 15.8041 21.7142 15.9267 21.6636 16.0399C21.613 16.1532 21.5394 16.2547 21.4475 16.3381C21.2622 16.5055 21.025 16.6043 20.7755 16.6181Z" fill="#182663"/>
              <path d="M24.8501 29.0786H16.4501H16.1281C15.9915 29.0359 15.8636 28.9696 15.7501 28.8826C15.6443 28.8055 15.558 28.7048 15.4981 28.5886C15.444 28.4848 15.4152 28.3696 15.4141 28.2526C15.4135 28.1308 15.4423 28.0108 15.4981 27.9026C15.558 27.7863 15.6443 27.6856 15.7501 27.6086C15.8636 27.5215 15.9915 27.4552 16.1281 27.4126H16.4501H24.9761C25.083 27.3989 25.1912 27.3989 25.2981 27.4126C25.4346 27.4552 25.5626 27.5215 25.6761 27.6086C25.7818 27.6856 25.8681 27.7863 25.9281 27.9026C25.9838 28.0108 26.0127 28.1308 26.0121 28.2526C26.011 28.3696 25.9822 28.4848 25.9281 28.5886C25.8681 28.7048 25.7818 28.8055 25.6761 28.8826C25.5626 28.9696 25.4346 29.0359 25.2981 29.0786C25.1912 29.0922 25.083 29.0922 24.9761 29.0786H24.8501Z" fill="#182663"/>
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            <NJFormItem
              value={tempMin.toString()}
              type="number"
              id="temperature_min"
              label={isSmall ? 'Température minimale (°C) *' : 'Température minimale (°C) (Obligatoire)'}
              hasError={isTempRangeInvalid}
              onChange={(e) => {
                const v = Number.parseFloat((e.target as HTMLInputElement).value);
                if (!Number.isNaN(v)) onTempMinChange(v);
              }}
            />
            <NJFormItem
              value={tempMax.toString()}
              type="number"
              id="temperature_max"
              label={isSmall ? 'Température maximale (°C) *' : 'Température maximale (°C) (Obligatoire)'}
              hasError={isTempRangeInvalid}
              subscriptMessage={
                isTempRangeInvalid
                  ? 'La température maximale doit être supérieure à la température minimale'
                  : ''
              }
              onChange={(e) => {
                const v = Number.parseFloat((e.target as HTMLInputElement).value);
                if (!Number.isNaN(v)) onTempMaxChange(v);
              }}
            />
          </div>
        </div>

        <div style={{ marginTop: SP16 }}>
          {/* NJFormItem for the value — same style as temperature fields */}
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: SP8 }}>
            <div style={{ flex: 1 }}>
              <NJFormItem
                value={rawNbSteps}
                type="number"
                id="nb_steps_input"
                label="Nombre de pas"
                hasError={nbStepsErrorMsg !== ''}
                subscriptMessage={nbStepsErrorMsg}
                onChange={(e) => {
                  const raw = (e.target as HTMLInputElement).value;
                  setRawNbSteps(raw);
                  const v = Number.parseInt(raw, 10);
                  if (!Number.isNaN(v) && v >= 1 && v <= 40) {
                    onNombreDePasChange(v);
                    setSliderKey((k) => k + 1);
                  }
                }}
              />
            </div>
          </div>
          {/* Slider synced with the input above */}
          <div style={{ marginTop: '4px', overflow: 'hidden' }}>
            <div style={{ marginTop: '-24px' }}>
              <NJSlider
                key={sliderKey}
                hasTooltip={isActive}
                id="nb_steps"
                label="Nombre de pas"
                max={40}
                min={1}
                name="nb_steps"
                step={1}
                value={nombreDePas}
                onChange={(v) => { if (v !== undefined) onNombreDePasChange(v); }}
              />
            </div>
          </div>
        </div>

        <div style={{ marginTop: SP16 }}>
          <NJText style={{ display: 'block', margin: 0, fontFamily: "'Lato', sans-serif", fontWeight: 500, color: '#334551' }}>
            Mode de régulation des sous-stations
          </NJText>
          <div style={{ marginTop: SP8 }}>
            <NJSegmentedControl
              label=""
              onChange={(v: string) => onRegulationModeChange(v as RegulationMode)}
              value={regulationMode}
              size="lg"
            >
              <NJSegmentedControlButton label="Delta T" value={DELTA_TEMPERATURE} />
              <NJSegmentedControlButton label="T Retour" value={OUTPUT_TEMPERATURE} />
            </NJSegmentedControl>
          </div>
        </div>

      </div>

      {/* ── Separator 2 ── */}
      <div className="general-separator" style={{ width: '1px', background: 'var(--nj-core-color-grey-300)', margin: '0 var(--nj-semantic-size-spacing-16)' }} />

      {/* ── Right column: Options d'étude ── */}
      <div className="general-right" style={{ flex: 1, padding: SP24 }}>
        <NJText style={{ fontFamily: "'Lato', sans-serif", display: 'block' }}>
          <strong>Options d'étude</strong>
        </NJText>
        <div style={{ marginTop: SP16 }}>
          <ToggleRow
            label="Dimensionnement des canalisations"
            checked={advancedOptions.pipes_sizing}
            onChange={(v) => onToggleOption('pipes_sizing', v)}
          />
          <ToggleRow
            label="Activation de la reserve de puissance"
            checked={advancedOptions.report_power_reserve}
            onChange={(v) => onToggleOption('report_power_reserve', v)}
          />
          <ToggleRow
            label="Activation du chemin critique"
            checked={advancedOptions.report_critical_path}
            onChange={(v) => onToggleOption('report_critical_path', v)}
          />
        </div>
      </div>
    </div>
  );
}

// ─── Tab 2: Températures et demande ──────────────────────────────────────────

function generateTempDemandRows(tempMin: number, tempMax: number, nSteps: number): TempDemandRow[] {
  const n = Math.max(1, Math.min(40, nSteps));
  if (n === 1) return [{ index: 1, tempSeche: tempMin, productionTotale: 0 }];
  return Array.from({ length: n }, (_, i) => {
    const t = tempMin + ((tempMax - tempMin) / (n - 1)) * i;
    return { index: i + 1, tempSeche: Math.round(t * 100) / 100, productionTotale: null };
  });
}

interface TempDemandTabProps {
  tempMin: number;
  tempMax: number;
  nombreDePas: number;
  onProductionValidityChange?: (isValid: boolean) => void;
}

function TempDemandTab({ tempMin, tempMax, nombreDePas, onProductionValidityChange }: TempDemandTabProps) {
  const [demandTab, setDemandTab] = useState<DemandTab>('consommation');
  const [foisonnementType, setFoisonnementType] = useState<FoisonnementKind>('consumption');
  const [coeffFoisonnement, setCoeffFoisonnement] = useState('1');
  const [vizMode, setVizMode] = useState<VizMode>('tableau');
  const [kbdOpen, setKbdOpen] = useState(false);

  const layoutRef = useRef<HTMLDivElement | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      layoutRef.current?.requestFullscreen();
    }
  }, []);

  const chartRoRef = useRef<ResizeObserver | null>(null);
  const [chartHeight, setChartHeight] = useState(400);
  const chartContainerRef = useCallback((node: HTMLDivElement | null) => {
    if (chartRoRef.current) { chartRoRef.current.disconnect(); chartRoRef.current = null; }
    if (!node) return;
    chartRoRef.current = new ResizeObserver(([entry]) => setChartHeight(entry.contentRect.height));
    chartRoRef.current.observe(node);
  }, []);

  const isProductionTab = demandTab === 'production';
  const isCoeffDisabled = isProductionTab || foisonnementType !== 'consumption';

  // ── Editable rows with undo/redo history ──────────────────────────────────
  const gridApiRef = useRef<GridApi | null>(null);
  const [localRows, setLocalRows] = useState<TempDemandRow[]>([]);
  const localRowsRef = useRef<TempDemandRow[]>([]);
  localRowsRef.current = localRows;
  const isProductionTabRef = useRef(isProductionTab);
  isProductionTabRef.current = isProductionTab;

  useEffect(() => {
    if (!onProductionValidityChange) return;
    const isValid = !isProductionTab || localRows.every(r => r.productionTotale !== null);
    onProductionValidityChange(isValid);
  }, [isProductionTab, localRows, onProductionValidityChange]);

  const undoStack = useRef<TempDemandRow[][]>([]);
  const redoStack = useRef<TempDemandRow[][]>([]);
  const [hasHistory, setHasHistory] = useState(false);
  const [hasFuture, setHasFuture] = useState(false);

  const [selectedColField, setSelectedColField] = useState<string | null>(null);
  const selectedColFieldRef = useRef<string | null>(null);
  selectedColFieldRef.current = selectedColField;
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const selectedRowsRef = useRef<Set<number>>(new Set());
  selectedRowsRef.current = selectedRows;
  const gridContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const generated = generateTempDemandRows(tempMin, tempMax, nombreDePas);
    localRowsRef.current = generated;
    setLocalRows([...generated]);
    undoStack.current = [];
    redoStack.current = [];
    setHasHistory(false);
    setHasFuture(false);
    setSelectedColField(null);
    setSelectedRows(new Set());
    selectedRowsRef.current = new Set();
    const api = gridApiRef.current;
    if (api) {
      api.forEachNode((node, i) => {
        if (generated[i] !== undefined) node.setData({ ...generated[i] });
      });
    }
  }, [tempMin, tempMax, nombreDePas]);

  const handleGridReady = useCallback((event: GridReadyEvent) => {
    gridApiRef.current = event.api;
  }, []);

  const applyRowsToGrid = useCallback((rows: TempDemandRow[]) => {
    const api = gridApiRef.current;
    if (!api) return;
    api.forEachNode((node, i) => {
      if (rows[i] !== undefined) node.setData({ ...rows[i] });
    });
  }, []);

  const handleCellValueChanged = useCallback((event: CellValueChangedEvent) => {
    if (event.oldValue === event.newValue) return;
    const field = event.colDef.field!;
    const rowIndex = event.rowIndex!;
    undoStack.current.push([...localRowsRef.current]);
    redoStack.current = [];
    const newVal = event.newValue === '' || event.newValue === null ? null : Number(event.newValue);
    const next = localRowsRef.current.map((r, i) =>
      i === rowIndex ? { ...r, [field]: newVal } : r
    );
    localRowsRef.current = next;
    setLocalRows(next);
    setHasHistory(true);
    setHasFuture(false);
  }, []);

  const handleUndo = useCallback(() => {
    const prev = undoStack.current.pop();
    if (!prev) return;
    redoStack.current.unshift([...localRowsRef.current]);
    localRowsRef.current = prev;
    setLocalRows([...prev]);
    applyRowsToGrid(prev);
    setHasHistory(undoStack.current.length > 0);
    setHasFuture(true);
  }, [applyRowsToGrid]);

  const handleRedo = useCallback(() => {
    const next = redoStack.current.shift();
    if (!next) return;
    undoStack.current.push([...localRowsRef.current]);
    localRowsRef.current = next;
    setLocalRows([...next]);
    applyRowsToGrid(next);
    setHasHistory(true);
    setHasFuture(redoStack.current.length > 0);
  }, [applyRowsToGrid]);

  const copyColumnToClipboard = useCallback(async (field: string) => {
    const tsv = localRowsRef.current
      .map(r => String(r[field as keyof TempDemandRow] ?? ''))
      .join('\n');
    await navigator.clipboard.writeText(tsv);
    NJToastService('La colonne a été copiée avec succès', {
      title: '',
      iconName: 'content_copy',
      hasCloseIcon: true,
    });
  }, []);

  const copyRowsToClipboard = useCallback(async (rowsToCopy: TempDemandRow[]) => {
    const prod = isProductionTabRef.current;
    const headers = prod
      ? ['#', 'Température sèche (°C)', 'Production totale (MW)']
      : ['#', 'Température sèche (°C)'];
    const tsv = [
      headers.join('\t'),
      ...rowsToCopy.map(r =>
        prod
          ? [r.index, r.tempSeche, r.productionTotale].join('\t')
          : [r.index, r.tempSeche].join('\t')
      ),
    ].join('\n');
    await navigator.clipboard.writeText(tsv);
    const total = localRowsRef.current.length;
    const n = rowsToCopy.length;
    const isFullTable = n === total;
    const msg = isFullTable
      ? 'Le tableau a été copié avec succès'
      : n === 1
      ? 'La ligne a été copiée avec succès'
      : `Les ${n} lignes ont été copiées avec succès`;
    NJToastService(msg, {
      title: '',
      iconName: 'content_copy',
      hasCloseIcon: true,
    });
  }, []);

  const downloadRowsAsCSV = useCallback(() => {
    const prod = isProductionTabRef.current;
    const headers = prod
      ? ['Température sèche (°C)', 'Production totale (MW)']
      : ['Température sèche (°C)'];
    const sheetData = [
      headers,
      ...localRowsRef.current.map(r =>
        prod
          ? [r.tempSeche, r.productionTotale ?? '']
          : [r.tempSeche]
      ),
    ];
    const ws = XLSX.utils.aoa_to_sheet(sheetData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Données');
    const filename = prod
      ? 'Template_temperature_production_hotwater.xlsx'
      : 'Template_temperature_hotwater.xlsx';
    XLSX.writeFile(wb, filename);
  }, []);

  const importFileRef = useRef<HTMLInputElement | null>(null);

  const handleImport = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const buffer = evt.target?.result;
      if (!buffer) return;
      const wb = XLSX.read(buffer, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rawRows = XLSX.utils.sheet_to_json<(string | number | null)[]>(ws, { header: 1 });
      const dataRows = rawRows.slice(1); // skip header
      const prod = isProductionTabRef.current;
      const current = localRowsRef.current;
      const newRows = current.map((r, i) => {
        if (i >= dataRows.length) return r;
        const cols = dataRows[i];
        const tempSeche = Number(cols[0]);
        if (isNaN(tempSeche)) return r;
        const updated: TempDemandRow = { ...r, tempSeche };
        if (prod) {
          const v = cols[1];
          updated.productionTotale = (v !== undefined && v !== null && v !== '') ? Number(v) : null;
        }
        return updated;
      });
      undoStack.current.push([...localRowsRef.current]);
      redoStack.current = [];
      localRowsRef.current = newRows;
      setLocalRows([...newRows]);
      applyRowsToGrid(newRows);
      setHasHistory(true);
      setHasFuture(false);
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  }, [applyRowsToGrid]);

  const handleGridContainerClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as Element;

    // Header click → column selection, clear row selection
    const headerCell = target.closest('.ag-header-cell[col-id]');
    if (headerCell) {
      const colId = headerCell.getAttribute('col-id');
      if (!colId) return;
      selectedRowsRef.current = new Set();
      setSelectedRows(new Set());
      if (colId === 'index') {
        setSelectedColField(prev => prev === '__all__' ? null : '__all__');
      } else {
        setSelectedColField(prev => prev === colId ? null : colId);
      }
      return;
    }

    // # cell click → row selection, clear column selection
    const indexCell = target.closest('.ag-cell[col-id="index"]');
    if (indexCell) {
      const rowEl = indexCell.closest('.ag-row');
      if (!rowEl) return;
      const rowIdx = parseInt(rowEl.getAttribute('row-index') ?? '-1', 10);
      if (rowIdx < 0) return;
      selectedColFieldRef.current = null;
      setSelectedColField(null);
      const next = new Set(selectedRowsRef.current);
      if (next.has(rowIdx)) next.delete(rowIdx);
      else next.add(rowIdx);
      selectedRowsRef.current = next;
      setSelectedRows(next);
      gridApiRef.current?.refreshCells({ force: true });
    }
  }, []);

  useEffect(() => {
    const container = gridContainerRef.current;
    if (!container) return;
    container.querySelectorAll<HTMLElement>('.ag-header-cell[col-id]').forEach((el) => {
      el.style.background = '';
    });
    if (selectedColField === '__all__') {
      container.querySelectorAll<HTMLElement>('.ag-header-cell[col-id]').forEach((el) => {
        if (el.getAttribute('col-id') !== 'index') el.style.background = '#DBEAFE';
      });
    } else if (selectedColField) {
      const el = container.querySelector<HTMLElement>(`.ag-header-cell[col-id="${selectedColField}"]`);
      if (el) el.style.background = '#DBEAFE';
    }
    gridApiRef.current?.refreshCells({ force: true });
  }, [selectedColField]);

  const pasteFromClipboard = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText();
      const lines = text.trim().split('\n').filter(Boolean);
      if (lines.length === 0) return;
      const startLine = isNaN(Number(lines[0].split('\t')[0])) ? 1 : 0;
      const dataLines = lines.slice(startLine);
      if (dataLines.length === 0) return;
      const prod = isProductionTabRef.current;
      const targetCol = selectedColFieldRef.current;
      const newRows = localRowsRef.current.map((r, i) => {
        if (i >= dataLines.length) return r;
        const cols = dataLines[i].split('\t');
        if (targetCol) {
          const val = Number(cols[0]);
          return isNaN(val) ? r : { ...r, [targetCol]: val };
        }
        // Detect format: internal copy includes index col → more columns
        const hasIndexCol = prod ? cols.length >= 3 : cols.length >= 2;
        const tempIdx = hasIndexCol ? 1 : 0;
        const prodIdx = hasIndexCol ? 2 : 1;
        const tempSeche = Number(cols[tempIdx]);
        const productionTotale = Number(cols[prodIdx]);
        return {
          ...r,
          ...(isNaN(tempSeche) ? {} : { tempSeche }),
          ...(prod && !isNaN(productionTotale) ? { productionTotale } : {}),
        };
      });
      undoStack.current.push([...localRowsRef.current]);
      redoStack.current = [];
      localRowsRef.current = newRows;
      setLocalRows([...newRows]);
      applyRowsToGrid(newRows);
      setHasHistory(true);
      setHasFuture(false);
    } catch { /* clipboard access denied */ }
  }, [applyRowsToGrid]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (document.activeElement?.tagName ?? '').toLowerCase();
      const isTyping = tag === 'input' || tag === 'textarea';
      const key = e.key.toLowerCase();

      // Delete / Backspace: clear selected column
      if (key === 'delete' || key === 'backspace') {
        const col = selectedColFieldRef.current;
        if (!col || col === '__all__' || isTyping) return;
        e.preventDefault();
        undoStack.current.push([...localRowsRef.current]);
        redoStack.current = [];
        const next = localRowsRef.current.map(r => ({ ...r, [col]: null }));
        localRowsRef.current = next;
        setLocalRows([...next]);
        applyRowsToGrid(next);
        setHasHistory(true);
        setHasFuture(false);
        return;
      }

      const ctrl = e.metaKey || e.ctrlKey;
      if (!ctrl) return;
      if (key === 'z' && !e.shiftKey) {
        if (isTyping) return;
        e.preventDefault();
        handleUndo();
      } else if ((key === 'z' && e.shiftKey) || key === 'y') {
        if (isTyping) return;
        e.preventDefault();
        handleRedo();
      } else if (key === 'c') {
        if (isTyping) return;
        const col = selectedColFieldRef.current;
        if (col === '__all__') {
          void copyRowsToClipboard(localRowsRef.current);
        } else if (col) {
          void copyColumnToClipboard(col);
        } else if (selectedRowsRef.current.size > 0) {
          const rows = localRowsRef.current.filter((_, i) => selectedRowsRef.current.has(i));
          void copyRowsToClipboard(rows);
        }
      } else if (key === 'a') {
        if (isTyping) return;
        e.preventDefault();
        selectedRowsRef.current = new Set();
        setSelectedRows(new Set());
        setSelectedColField('__all__');
      } else if (key === 'v') {
        if (isTyping) return;
        e.preventDefault();
        void pasteFromClipboard();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleUndo, handleRedo, copyRowsToClipboard, copyColumnToClipboard, pasteFromClipboard, applyRowsToGrid]);

  const chartData = useMemo<ChartData<'bar' | 'line'>>(
    () => ({
      labels: localRows.map((r) => String(r.tempSeche)),
      datasets: [
        ...(isProductionTab
          ? [
              {
                type: 'bar' as const,
                label: 'Production totale (MW)',
                data: localRows.map((r) => r.productionTotale),
                backgroundColor: 'rgba(63, 81, 181, 0.8)',
                borderColor: '#3f51b5',
                borderWidth: 1,
                borderRadius: 2,
                pointStyle: 'rect' as const,
                yAxisID: 'yProd',
                order: 2,
              },
            ]
          : []),
        {
          type: 'line' as const,
          label: 'Température sèche (°C)',
          data: localRows.map((r) => r.tempSeche),
          borderColor: '#D55E00',
          backgroundColor: '#fff3e0',
          borderWidth: 2,
          pointRadius: 5,
          pointHoverRadius: 7,
          pointStyle: 'circle' as const,
          pointBorderWidth: 2,
          tension: 0.3,
          fill: false,
          yAxisID: 'yTemp',
          order: 1,
        },
      ],
    }),
    [localRows, isProductionTab],
  );

  const chartOptions = useMemo<ChartOptions<'bar' | 'line'>>(
    () => ({
      responsive: true,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            usePointStyle: true,
            padding: 16,
          },
        },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const v = ctx.parsed.y;
              if (v === null || v === undefined) return '';
              const unit = (ctx.dataset as { yAxisID?: string }).yAxisID === 'yProd' ? ' MW' : ' °C';
              return `${ctx.dataset.label ?? ''} : ${v.toFixed(2)}${unit}`;
            },
          },
        },
      },
      scales: {
        x: {
          title: {
            display: true,
            text: 'Température sèche (°C)',
            font: { size: 12 },
            color: '#212121',
          },
          grid: { color: '#f0f0f0' },
        },
        yTemp: {
          type: 'linear',
          position: 'left',
          title: {
            display: true,
            text: 'Température (°C)',
            font: { size: 12 },
            color: '#212121',
          },
          grid: { color: '#f0f0f0' },
        },
        ...(isProductionTab
          ? {
              yProd: {
                type: 'linear',
                position: 'right',
                beginAtZero: true,
                title: {
                  display: true,
                  text: 'Production (MW)',
                  font: { size: 12 },
                  color: '#212121',
                },
                ticks: { color: '#616161' },
                grid: { drawOnChartArea: false },
              },
            }
          : {}),
      },
    }),
    [isProductionTab],
  );

  const colDefs = useMemo<ColDef<TempDemandRow>[]>(() => [
    {
      field: 'index',
      headerName: '#',
      width: 70,
      maxWidth: 70,
      editable: false,
      sortable: false,
      filter: false,
      cellClass: getCellClassName({ readOnly: true }),
      cellStyle: { textAlign: 'center', backgroundColor: '#F0F0F0', color: '#8C9AA6' },
      headerClass: 'ag-header-cell-center index-col-header',
    },
    {
      field: 'tempSeche',
      headerName: 'Température sèche (°C)',
      flex: 1,
      editable: true,
      filter: 'agTextColumnFilter',
      cellClass: getCellClassName({ readOnly: false }),
      cellStyle: (params: { rowIndex: number | null }) => {
        const rowSel = params.rowIndex !== null && selectedRowsRef.current.has(params.rowIndex);
        const colSel = selectedColFieldRef.current === 'tempSeche' || selectedColFieldRef.current === '__all__';
        return {
          textAlign: 'center' as const,
          ...(rowSel ? { backgroundColor: '#F0FDF4' } : colSel ? { backgroundColor: '#DBEAFE' } : {}),
        };
      },
      headerClass: 'ag-header-cell-center',
      valueFormatter: ({ value }: { value: number | null }) =>
        value === null || value === undefined ? '-' : String(value),
    },
    ...(isProductionTab ? [{
      field: 'productionTotale' as const,
      headerName: 'Production totale (MW)',
      flex: 1,
      editable: true,
      cellClass: getCellClassName({ readOnly: false }),
      cellStyle: (params: { rowIndex: number | null }) => {
        const rowSel = params.rowIndex !== null && selectedRowsRef.current.has(params.rowIndex);
        const colSel = selectedColFieldRef.current === 'productionTotale' || selectedColFieldRef.current === '__all__';
        return {
          textAlign: 'center' as const,
          ...(rowSel ? { backgroundColor: '#F0FDF4' } : colSel ? { backgroundColor: '#DBEAFE' } : {}),
        };
      },
      headerClass: 'ag-header-cell-center',
      valueFormatter: ({ value }: { value: number | null }) =>
        value === null || value === undefined ? '-' : String(value),
    }] : []),
  ], [isProductionTab]);

  return (
    <div className="temp-demand-layout" style={{ display: 'flex', minHeight: '600px', height: '100%' }}>

      {/* ── Left: Paramétrage des données d'entrées ── */}
      <div className="temp-demand-left" style={{
        width: '410px',
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        borderRight: '1px solid var(--nj-semantic-color-border-secondary-default)',
      }}>
        {/* Title — outside scroll, mirrors right column header */}
        <div style={{ padding: `${SP24} ${SP24} 0 ${SP24}`, marginBottom: SP16 }}>
          <NJText style={{ fontWeight: 700, margin: 0, fontFamily: "'Lato', sans-serif" }}>
            Paramétrage des données d'entrées
          </NJText>
        </div>

        {/* Scrollable form content */}
        <div style={{ padding: `0 ${SP24} ${SP24} ${SP24}` }}>

          {/* Section label: Définition de la demande + info tooltip */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: SP8 }}>
            <NJText scale="sm" style={{ fontWeight: 400, margin: 0, fontFamily: "'Lato', sans-serif", color: 'var(--nj-semantic-color-text-primary-default)' }}>
              Définition de la demande
            </NJText>
            <NJTooltip
              text={
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '14px', lineHeight: '1.5', fontFamily: "'Lato', sans-serif", color: '#FFFFFF', textAlign: 'left' }}>
                  <div>
                    <div style={{ fontWeight: 700, marginBottom: '4px', fontSize: '14px' }}>Consommation</div>
                    <div style={{ fontWeight: 400, fontSize: '14px' }}>Fixer la consommation de chaque point de livraison.</div>
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, marginBottom: '4px', fontSize: '14px' }}>Production</div>
                    <div style={{ fontWeight: 400, fontSize: '14px' }}>Fixer la puissance totale injectée sur le réseau. Celle-ci sera répartie sur chaque point de livraison en fonction de leur consommation.</div>
                  </div>
                </div>
              }
              hasArrow
              placement="top"
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', width: '16px', height: '16px' }}>
                <NJIcon name="info_outlined" scale="sm" />
              </span>
            </NJTooltip>
          </div>

          {/* Consommation / Production toggle (Figma segmented-control) */}
          <div style={{ marginBottom: SP16 }}>
            <NJSegmentedControl
              label=""
              value={demandTab}
              onChange={(v) => setDemandTab(v as DemandTab)}
              size="lg"
            >
              <NJSegmentedControlButton label="Consommation" value="consommation" />
              <NJSegmentedControlButton label="Production" value="production" />
            </NJSegmentedControl>
          </div>

          {/* ── Températures — carte "input optimisations" (378×120) ── */}
          <div style={{
            width: '378px',
            height: '133px',
            flexShrink: 0,
            flexGrow: 0,
            boxSizing: 'border-box',
            border: 'none',
            borderRadius: '4px',
            background: '#F6F8F9',
            padding: '16px',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
            marginBottom: SP16,
            overflow: 'hidden',
          }}>
            {/* Upload Température — 346×101, flex col, gap:12 */}
            <div style={{
              width: '346px',
              height: '101px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
              padding: 0,
              gap: '12px',
              flexShrink: 0,
              flexGrow: 0,
            }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <circle cx="12" cy="12" r="12" fill="white"/>
                <g clipPath="url(#clip0_2353_10691)">
                  <path d="M15 13V5C15 3.34 13.66 2 12 2C10.34 2 9 3.34 9 5V13C7.79 13.91 7 15.37 7 17C7 19.76 9.24 22 12 22C14.76 22 17 19.76 17 17C17 15.37 16.21 13.91 15 13ZM11 5C11 4.45 11.45 4 12 4C12.55 4 13 4.45 13 5H12V6H13V8H12V9H13V11H11V5Z" fill="#171D21"/>
                </g>
                <defs>
                  <clipPath id="clip0_2353_10691">
                    <rect width="24" height="24" fill="white"/>
                  </clipPath>
                </defs>
              </svg>
              <NJText style={{ fontWeight: 700, margin: 0, fontFamily: "'Lato', sans-serif" }}>
                {demandTab === 'production' ? 'Températures et Production' : 'Températures'}
              </NJText>
              <NJTooltip
                text={
                  demandTab === 'production' ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '14px', lineHeight: '1.5', fontFamily: "'Lato', sans-serif", color: '#FFFFFF', textAlign: 'left' }}>
                      <div style={{ fontWeight: 500, fontSize: '14px' }}>Fixer les valeurs de température sèche et production totale prises en compte par l'optimisation.</div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '14px', lineHeight: '1.5', fontFamily: "'Lato', sans-serif", color: '#FFFFFF', textAlign: 'left' }}>
                      <div style={{ fontWeight: 500, fontSize: '14px' }}>Fixer les valeurs de température sèche prises en compte par l'optimisation.</div>
                    </div>
                  )
                }
                hasArrow
                placement="right"
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', width: '16px', height: '16px', marginLeft: '8px' }}>
                <NJIcon name="info_outlined" scale="sm" />
              </span>
              </NJTooltip>
            </div>
            <div style={{
              width: '309px',
              height: '20px',
              fontFamily: "'Lato', sans-serif",
              fontStyle: 'normal',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '20px',
              display: 'flex',
              alignItems: 'center',
              color: '#60798B',
              flexShrink: 0,
              flexGrow: 0,
            }}>
              {demandTab === 'production' ? 'Températures sèches et production totale' : 'Températures sèches'}
            </div>
            <input
              ref={importFileRef}
              type="file"
              accept=".xlsx,.xls"
              style={{ display: 'none' }}
              onChange={handleImport}
            />
            <NJButton
              label="Choisir un fichier"
              variant="primary"
              scale="md"
              icon="link"
              onClick={() => importFileRef.current?.click()}
              style={{
                width: '200px',
                height: '40px',
                borderRadius: '0px',
                background: '#007ACD',
                flexShrink: 0,
                flexGrow: 0,
              }}
            />
            </div>
          </div>

          {/* ── Consommation section ── */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            padding: '16px',
            gap: '12px',
            width: '378px',
            height: '280px',
            background: '#F6F8F9',
            flexShrink: 0,
            flexGrow: 0,
            alignSelf: 'stretch',
            boxSizing: 'border-box',
          }}>
            {/* Upload — 346×248, flex col, gap:12 */}
            <div style={{
              width: '346px',
              height: '248px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              padding: 0,
              gap: '12px',
              flexShrink: 0,
              flexGrow: 0,
              alignSelf: 'stretch',
            }}>
            <div style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-start',
              alignItems: 'center',
              padding: 0,
              gap: '8px',
              width: '167px',
              height: '20px',
              flexShrink: 0,
              flexGrow: 0,
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <circle cx="12" cy="12" r="12" fill="white"/>
                <g clipPath="url(#clip0_2330_60800)">
                  <path d="M12 5.69L17 10.19V18H15V12H9V18H7V10.19L12 5.69ZM12 3L2 12H5V20H11V14H13V20H19V12H22L12 3Z" fill="#171D21"/>
                </g>
                <defs>
                  <clipPath id="clip0_2330_60800">
                    <rect width="24" height="24" fill="white"/>
                  </clipPath>
                </defs>
              </svg>
              <NJText style={{ fontWeight: 700, margin: 0, fontFamily: "'Lato', sans-serif" }}>
                Consommation
              </NJText>
            </div>

            {/* Type de consommation */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: SP8 }}>
                <NJText scale="sm" style={{ fontWeight: 400, margin: 0, fontFamily: "'Lato', sans-serif", color: 'var(--nj-semantic-color-text-primary-default)' }}>Type de consommation</NJText>
                <NJTooltip
                  text={
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '14px', lineHeight: '1.5', fontFamily: "'Lato', sans-serif", color: '#FFFFFF', textAlign: 'left' }}>
                      <div>
                        <div style={{ fontWeight: 700, marginBottom: '4px', fontSize: '14px' }}>Profils</div>
                        <div style={{ fontWeight: 400, fontSize: '14px' }}>Calculer les consommations des points de livraison à partir des profils définis sur les points de livraison en fonction de la température extérieure.</div>
                      </div>
                      <div>
                        <div style={{ fontWeight: 700, marginBottom: '4px', fontSize: '14px' }}>Fichier</div>
                        <div style={{ fontWeight: 400, fontSize: '14px' }}>Fixer la consommation par heure et par client via un fichier de consommations.</div>
                      </div>
                    </div>
                  }
                  hasArrow
                  placement="right"
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', width: '16px', height: '16px' }}>
                <NJIcon name="info_outlined" scale="sm" />
              </span>
                </NJTooltip>
              </div>
              {/* Radio group: Profils simples + Fichier (disabled) */}
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
                <input
                  type="radio"
                  id="profils-simples"
                  name="consommation-type"
                  defaultChecked
                  readOnly
                  style={{ accentColor: '#0052CC', width: '16px', height: '16px', margin: 0, cursor: 'default', flexShrink: 0 }}
                />
                <label
                  htmlFor="profils-simples"
                  style={{ fontSize: '14px', fontFamily: "'Lato', sans-serif", color: '#171D21', lineHeight: '20px', cursor: 'default', marginRight: '12px' }}
                >
                  Profils simples
                </label>
                <input
                  type="radio"
                  id="fichier"
                  name="consommation-type"
                  disabled
                  style={{ width: '16px', height: '16px', margin: 0, cursor: 'not-allowed', flexShrink: 0 }}
                />
                <label
                  htmlFor="fichier"
                  style={{ fontSize: '14px', fontFamily: "'Lato', sans-serif", color: '#9e9e9e', lineHeight: '20px', cursor: 'not-allowed' }}
                >
                  Fichier
                </label>
              </div>
            </div>

            {/* Type de foisonnement */}
            <div>
              <NJText scale="sm" style={{ fontWeight: 400, margin: 0, fontFamily: "'Lato', sans-serif", marginBottom: SP8, display: 'block', color: 'var(--nj-semantic-color-text-primary-default)' }}>
                Type de foisonnement
              </NJText>
              <NJSegmentedControl
                label=""
                value={foisonnementType}
                onChange={(v) => setFoisonnementType(v as FoisonnementKind)}
                size="lg"
              >
                <NJSegmentedControlButton label="Automatique" value="automatic" />
                <NJSegmentedControlButton label="Canalisation" value="canalisation" />
                <NJSegmentedControlButton label="Consommation" value="consumption" />
              </NJSegmentedControl>
            </div>

            {/* Coefficient de foisonnement */}
            <div style={{ width: '100%' }}>
              <NJText scale="sm" style={{ fontWeight: 400, margin: 0, fontFamily: "'Lato', sans-serif", marginBottom: SP8, display: 'block', color: 'var(--nj-semantic-color-text-primary-default)' }}>
                Coefficient de foisonnement (entre 0 et 1)
              </NJText>
              <div style={{ position: 'relative', width: '100%' }}>
                <input
                  id="coeff-foisonnement"
                  type="number"
                  value={isCoeffDisabled ? '1' : coeffFoisonnement}
                  disabled={isCoeffDisabled}
                  onChange={(e) => {
                    if (!isCoeffDisabled) setCoeffFoisonnement(e.target.value);
                  }}
                  style={{
                    width: '100%',
                    height: '40px',
                    border: '1px solid #CCD4DA',
                    borderRadius: '0px',
                    padding: '8px 36px 8px 12px',
                    fontSize: '14px',
                    fontFamily: "'Lato', sans-serif",
                    color: '#171D21',
                    background: isCoeffDisabled ? '#F6F8F9' : '#ffffff',
                    boxSizing: 'border-box',
                    outline: 'none',
                  }}
                />
                <div style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'auto' }}>
                  <NJTooltip
                    text={
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '14px', lineHeight: '1.5', fontFamily: "'Lato', sans-serif", color: '#FFFFFF', textAlign: 'left' }}>
                        <div style={{ fontWeight: 400, fontSize: '14px' }}>Ce coefficient (0 à 1) ajuste la consommation de chaque point de livraison pour simuler des scénarios avec foisonnement.</div>
                        <div>
                          <div style={{ fontWeight: 700, marginBottom: '8px', fontSize: '14px' }}>Exemples :</div>
                          <ul style={{ margin: 0, paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '14px', fontWeight: 400 }}>
                            <li>1 = inchangé</li>
                            <li>0.8 = réduction de 20 %</li>
                            <li>0.5 = réduction de 50 %</li>
                          </ul>
                        </div>
                      </div>
                    }
                    hasArrow
                    placement="right"
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', width: '16px', height: '16px' }}>
                <NJIcon name="info_outlined" scale="sm" />
              </span>
                  </NJTooltip>
                </div>
              </div>
            </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right: Visualisation des données d'entrée */}
      <div ref={layoutRef} className="temp-demand-right" style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: SP24, minWidth: 0 }}>
        <NJToastContainer />

        {/* Header row */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: SP16,
          flexWrap: 'wrap',
          gap: '8px',
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {/* Row 1: title + row count + keyboard shortcut */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <NJText style={{ fontWeight: 700, margin: 0, fontFamily: "'Lato', sans-serif" }}>
                Visualisation des données d&apos;entrée
              </NJText>
              {localRows.length > 0 && (
                <span style={{
                  fontSize: '12px', color: 'var(--nj-semantic-color-text-secondary-default)',
                  background: '#F6F8F9', padding: '2px 8px', borderRadius: '12px',
                  border: '1px solid #CCD4DA', fontFamily: "'Lato', sans-serif",
                }}>
                  {localRows.length} lignes
                </span>
              )}
              <span
                style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
                onMouseEnter={() => setKbdOpen(true)}
                onMouseLeave={() => setKbdOpen(false)}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
                  <span className="material-icons" style={{ color: '#171d21', fontSize: '20px', lineHeight: 1 }}>keyboard</span>
                </span>
              {kbdOpen && (
                <div style={{
                  position: 'absolute',
                  top: 'calc(100% + 8px)',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 999,
                  background: '#212121',
                  borderRadius: '6px',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
                  padding: '14px 16px',
                  width: '360px',
                  fontFamily: "'Lato', sans-serif",
                  pointerEvents: 'none',
                }}>
                  <div style={{ fontWeight: 700, fontSize: '13px', color: '#fff', marginBottom: '10px' }}>
                    Raccourcis clavier
                  </div>
                  {([
                    { keys: ['Clic'],               desc: 'sur en-tête — Sélectionner une colonne' },
                    { keys: ['⌘A', 'Ctrl+A'],       desc: '— Sélectionner tout le tableau' },
                    { keys: ['⌘C', 'Ctrl+C'],       desc: '— Copier la sélection' },
                    { keys: ['⌘V', 'Ctrl+V'],       desc: '— Coller dans la colonne sélectionnée' },
                    { keys: ['Delete', 'Backspace'], desc: '— Vider la colonne sélectionnée' },
                    { keys: ['⌘Z', 'Ctrl+Z'],       desc: '— Annuler la dernière action' },
                    { keys: ['⌘Y', 'Ctrl+Y'],       desc: "— Rétablir l'action annulée" },
                    { keys: ['Double-clic'],         desc: 'sur cellule — Éditer' },
                    { keys: ['Enter'],               desc: '— Valider et passer à la ligne suivante' },
                    { keys: ['Échap'],               desc: '— Annuler la saisie' },
                  ] as { keys: string[]; desc: string }[]).map(({ keys, desc }) => (
                    <div key={keys[0]} style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '7px', fontSize: '12px', color: '#e0e0e0', flexWrap: 'wrap' }}>
                      {keys.map((k, i) => (
                        <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          <span style={{
                            display: 'inline-block', padding: '2px 7px',
                            fontSize: '11px', fontFamily: 'monospace',
                            background: 'rgba(255,255,255,0.1)',
                            border: '1px solid rgba(255,255,255,0.35)',
                            borderRadius: '4px', color: '#fff',
                            lineHeight: '18px', whiteSpace: 'nowrap',
                          }}>{k}</span>
                          {i < keys.length - 1 && <span style={{ color: '#9e9e9e' }}>/</span>}
                        </span>
                      ))}
                      <span>{desc}</span>
                    </div>
                  ))}
                </div>
              )}
            </span>
            </div>{/* end Row 1 */}

            {/* Row 2: warning badges — only rendered when data is incomplete */}
            {(() => {
              const filledTemp = localRows.filter(r => r.tempSeche !== null).length;
              const filledProd = localRows.filter(r => r.productionTotale !== null).length;
              const tempIncomplete = localRows.length > 0 && filledTemp < localRows.length;
              const prodIncomplete = isProductionTab && localRows.length > 0 && filledProd < localRows.length;
              if (!tempIncomplete && !prodIncomplete) return null;
              const warningStyle: CSSProperties = {
                fontSize: '12px', color: '#B45309', background: '#FFFBEB',
                padding: '2px 8px', borderRadius: '12px', border: '1px solid #FCD34D',
                fontFamily: "'Lato', sans-serif", display: 'inline-flex', alignItems: 'center', gap: '4px',
              };
              return (
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px' }}>
                  {tempIncomplete && (
                    <span style={warningStyle}>
                      <span className="material-icons" style={{ fontSize: '13px' }}>warning_amber</span>
                      {filledTemp}/{localRows.length} température renseignée
                    </span>
                  )}
                  {prodIncomplete && (
                    <span style={warningStyle}>
                      <span className="material-icons" style={{ fontSize: '13px' }}>warning_amber</span>
                      {filledProd}/{localRows.length} production renseignée
                    </span>
                  )}
                </div>
              );
            })()}
          </div>{/* end column container */}

          <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
            <NJIconButton icon="download" label="Télécharger" variant="primary" scale="xs" onClick={downloadRowsAsCSV} />
            <NJIconButton icon="content_copy" label="Copier" variant="primary" scale="xs" onClick={() => void copyRowsToClipboard(localRows)} />
            <NJIconButton icon="undo" label="Annuler" variant="primary" scale="xs" onClick={handleUndo} isDisabled={!hasHistory} />
            <NJIconButton icon="redo" label="Rétablir" variant="primary" scale="xs" onClick={handleRedo} isDisabled={!hasFuture} />
            <div style={{ width: '1px', height: '20px', background: 'var(--nj-semantic-color-border-secondary-default)', margin: '0 6px' }} />
            <NJSegmentedControl
              label="Mode de visualisation"
              value={vizMode}
              onChange={(v) => {
                if (v === 'graphique') setSelectedColField(null);
                setVizMode(v as VizMode);
              }}
              size="sm"
            >
              <NJSegmentedControlButton label="Tableau" value="tableau" iconName="table_chart" />
              <NJSegmentedControlButton label="Graphique" value="graphique" iconName="bar_chart" />
            </NJSegmentedControl>
            <NJIconButton icon={isFullscreen ? 'fullscreen_exit' : 'fullscreen'} label={isFullscreen ? 'Quitter le plein écran' : 'Plein écran'} variant="primary" scale="xs" onClick={toggleFullscreen} />
          </div>
        </div>

        {/* Content: table or chart */}
        {vizMode === 'tableau' ? (
          <div
            ref={gridContainerRef}
            onClick={handleGridContainerClick}
            style={{
              flex: 1,
              minHeight: 0,
              background: 'var(--nj-semantic-color-background-neutral-secondary-default)',
              borderRadius: 'var(--nj-core-border-radius-200)',
              padding: '12px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <DataTable<TempDemandRow>
              testId="temp-demand-table"
              data={localRows}
              verticalScroll
              header={colDefs}
              defaultColDef={{ flex: 1, sortable: false, filter: true, resizable: true, minWidth: 150 }}
              onGridReady={handleGridReady}
              onCellValueChanged={handleCellValueChanged}
            />
          </div>
        ) : (
          <div ref={chartContainerRef} style={{ flex: 1, minHeight: 0 }}>
          <MixedChart type="mixed" data={chartData} options={chartOptions} heightPx={chartHeight} />
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Tab 3: Planning ──────────────────────────────────────────────────────────

function PlanningTab() {
  const { data, isLoading, isError } = usePlannings();
  const [selectedPlannings, setSelectedPlannings] = useState<Set<string>>(new Set());

  const togglePlanning = (id: string) => {
    setSelectedPlannings((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const plannings = data ?? { production: [], topology: [] };
  const selectedNames = [...selectedPlannings].map((id) => {
    const all = [...plannings.production, ...plannings.topology];
    return all.find((p) => p.id === id)?.label ?? id;
  });

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
        <NJSpinner />
      </div>
    );
  }

  if (isError) {
    return (
      <NJInlineMessage variant="error" title="Erreur de chargement">
        Impossible de charger les plannings.
      </NJInlineMessage>
    );
  }

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '280px 1fr',
      gap: SP24,
      minHeight: '420px',
      padding: SP24,
    }}>
      {/* Sidebar */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--nj-core-space-500)',
        paddingRight: SP24,
        borderRight: '1px solid var(--nj-semantic-color-border-secondary-default)',
      }}>
        <div>
          <p style={{
            margin: `0 0 ${SP8} 0`,
            fontSize: '12px',
            fontWeight: 'var(--nj-core-font-weight-semibold)',
            color: 'var(--nj-semantic-color-text-secondary-default)',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}>
            Planning de production
          </p>
          {plannings.production.length === 0 ? (
            <NJText scale="sm" style={{ color: 'var(--nj-semantic-color-text-secondary-default)' }}>
              Il n'y a pas de planning de production disponible pour cette optimisation
            </NJText>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {plannings.production.map((p) => (
                <NJCheckbox
                  key={p.id}
                  label={p.label}
                  isChecked={selectedPlannings.has(p.id)}
                  onChange={() => togglePlanning(p.id)}
                />
              ))}
            </div>
          )}
        </div>
        <div>
          <p style={{
            margin: `0 0 ${SP8} 0`,
            fontSize: '12px',
            fontWeight: 'var(--nj-core-font-weight-semibold)',
            color: 'var(--nj-semantic-color-text-secondary-default)',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}>
            Planning de topologie
          </p>
          {plannings.topology.length === 0 ? (
            <NJText scale="sm" style={{ color: 'var(--nj-semantic-color-text-secondary-default)' }}>
              Il n'y a pas de planning de topologie disponible pour cette optimisation
            </NJText>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {plannings.topology.map((p) => (
                <NJCheckbox
                  key={p.id}
                  label={p.label}
                  isChecked={selectedPlannings.has(p.id)}
                  onChange={() => togglePlanning(p.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Main content */}
      <div>
        {selectedPlannings.size === 0 ? (
          <NJInlineMessage variant="information" title="Veuillez sélectionner un planning">
            Sélectionnez un ou plusieurs plannings dans la liste de gauche.
          </NJInlineMessage>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: SP16 }}>
            <NJText as="p" scale="sm" style={{ margin: 0 }}>
              <strong>{selectedPlannings.size}</strong>{' '}
              planning{selectedPlannings.size > 1 ? 's' : ''} sélectionné{selectedPlannings.size > 1 ? 's' : ''}{' '}
              : {selectedNames.join(', ')}
            </NJText>
            <div style={{
              border: '1px solid var(--nj-semantic-color-border-secondary-default)',
              borderRadius: 'var(--nj-core-border-radius-200)',
              padding: SP16,
              background: '#F6F8F9',
              overflowX: 'auto',
            }}>
              <div style={{
                display: 'flex', gap: SP16,
                marginBottom: SP16, paddingBottom: '8px',
                borderBottom: '1px solid var(--nj-semantic-color-border-secondary-default)',
              }}>
                <NJText scale="xs" style={{ margin: 0, color: 'var(--nj-semantic-color-text-secondary-default)', width: '160px', flexShrink: 0 }}>
                  Actif de production
                </NJText>
                {[2020, 2025, 2030, 2035, 2040, 2045].map((y) => (
                  <NJText key={y} scale="xs" style={{ margin: 0, flex: 1, color: 'var(--nj-semantic-color-text-secondary-default)' }}>
                    {y}
                  </NJText>
                ))}
              </div>
              {['Chaufferie biomasse', 'Chaudière gaz', 'PAC base', 'Pompe de distribution', 'Secours fioul'].map((asset) => (
                <div key={asset} style={{
                  display: 'flex', gap: SP16,
                  paddingBlock: '6px',
                  borderBottom: '1px solid var(--nj-semantic-color-border-secondary-default)',
                }}>
                  <NJText scale="xs" style={{ margin: 0, width: '160px', flexShrink: 0 }}>{asset}</NJText>
                  {[2020, 2025, 2030, 2035, 2040, 2045].map((y) => (
                    <div key={y} style={{ flex: 1 }} />
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Tab 3: Options avancées ──────────────────────────────────────────────────

type OptionEntry = { key: keyof AdvancedOptions; label: string; section: string };

const LEFT_OPTIONS: OptionEntry[] = [
  { key: 'diameter_homogenization', label: 'Homogénéisation diamètres aller et retour',    section: 'Dimensionnement' },
  { key: 'reduce_network',          label: 'Réduction réseau (canalisations)',              section: 'Dimensionnement' },
  { key: 'delta_t_min',             label: 'Delta T minimum',                               section: 'Sous-Stations' },
  { key: 'total_production_correction', label: 'Correction de la production totale',        section: 'Sous-Stations' },
  { key: 'ets_flow_correction',     label: 'Correction des débits en sous-stations',        section: 'Sous-Stations' },
];

const RIGHT_OPTIONS: OptionEntry[] = [
  { key: 'generate_influence_zones_files',   label: "Générer les fichiers des zones d'influence", section: 'Résultats' },
  { key: 'generate_limiting_factors_report', label: 'Générer le fichier des facteurs limitants',  section: 'Résultats' },
  { key: 'iis',                              label: 'Générer IIS si infaisable',                    section: 'Résultats' },
  { key: 'short',                            label: 'Short',                                        section: 'Autre' },
  { key: 'test_relax_uc',                    label: 'Relaxation variables binaires UC',             section: 'Débogage' },
  { key: 'test_feasopt',                     label: 'Activer FeasOpt',                              section: 'Débogage' },
  { key: 'relax_regulators',                 label: 'Relaxation variables binaires régulateurs',    section: 'Débogage' },
  { key: 'debug_generate_logs',              label: 'Activer mode debug',                           section: 'Débogage' },
  { key: 'generate_support_reports',         label: 'Rapport de débug avancé',                     section: 'Débogage' },
  { key: 'two_stage_solve_lt',               label: 'Imposer 2 étapes au tirs LT',                 section: 'Débogage' },
];

function renderOptionSections(
  entries: OptionEntry[],
  values: AdvancedOptions,
  onToggle: (key: keyof AdvancedOptions, value: boolean) => void,
) {
  const sections = [...new Set(entries.map((o) => o.section))];
  return sections.map((section) => (
    <ToggleSection key={section} title={section}>
      {entries
        .filter((o) => o.section === section)
        .map((o) => (
          <ToggleRow
            key={o.key}
            label={o.label}
            checked={values[o.key]}
            onChange={(v) => onToggle(o.key, v)}
          />
        ))}
    </ToggleSection>
  ));
}

interface AdvancedOptionsTabProps {
  options: AdvancedOptions;
  onToggle: (key: keyof AdvancedOptions, value: boolean) => void;
}
function AdvancedOptionsTab({ options, onToggle }: AdvancedOptionsTabProps) {
  return (
    <div className="advanced-layout" style={{
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: 'var(--nj-core-space-800)',
      alignItems: 'start',
      padding: SP24,
    }}>
      <div>{renderOptionSections(LEFT_OPTIONS, options, onToggle)}</div>
      <div>{renderOptionSections(RIGHT_OPTIONS, options, onToggle)}</div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function NewOptiFormTimeless() {
  const [activeTab, setActiveTab] = useState<TabId>('general');

  // Général
  const [nom, setNom] = useState('');
  const [description, setDescription] = useState('');
  const [tempMin, setTempMin] = useState(-5);
  const [tempMax, setTempMax] = useState(18);
  const [nombreDePas, setNombreDePas] = useState(2);
  const [regulationMode, setRegulationMode] = useState<RegulationMode>(DELTA_TEMPERATURE as RegulationMode);

  // Options avancées
  const [advancedOptions, setAdvancedOptions] = useState<AdvancedOptions>(DEFAULT_ADVANCED_OPTIONS);
  const [isProductionDataValid, setIsProductionDataValid] = useState(true);

  const activeTabIndex = TABS.findIndex((t) => t.id === activeTab);
  const isFirstTab = activeTabIndex === 0;
  const isLastTab  = activeTabIndex === TABS.length - 1;

  const [visitedTabs, setVisitedTabs] = useState<Set<TabId>>(new Set(['general']));
  useEffect(() => {
    setVisitedTabs((prev) => (prev.has(activeTab) ? prev : new Set([...prev, activeTab])));
  }, [activeTab]);

  const goToPrev = () => { if (activeTabIndex > 0) setActiveTab(TABS[activeTabIndex - 1].id); };
  const goToNext = () => { if (activeTabIndex < TABS.length - 1) setActiveTab(TABS[activeTabIndex + 1].id); };
  const isSmall = useIsSmallScreen();

  const toggleAdvancedOption = (key: keyof AdvancedOptions, value: boolean) => {
    setAdvancedOptions((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <PrototypeStudioLayout title="Formulaire de création d'une optimisation paramétrique atemporelle">
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        <div className="opti-form-card" style={{
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 80px)',
          minHeight: '640px',
          background: 'var(--nj-semantic-color-background-primary-default)',
          border: '1px solid var(--nj-semantic-color-border-secondary-default)',
          borderRadius: 'var(--nj-core-border-radius-300)',
          boxShadow: 'var(--nj-core-shadow-200)',
          overflow: 'hidden',
        }}>
          {/* ── Header ── */}
          <div className="opti-form-header" style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '48px',
            padding: '16px 24px',
            height: '64px',
            flexShrink: 0,
            background: '#F6F8F9',
            borderBottom: '1px solid #CCD4DA',
            boxSizing: 'border-box',
          }}>
            <span className="opti-form-header-title" style={{
              fontFamily: "'Lato', sans-serif",
              fontWeight: 700,
              fontSize: '16px',
              lineHeight: '24px',
              color: 'var(--nj-semantic-color-text-primary-default)',
              flexShrink: 0,
            }}>
              Formulaire de création d'une optimisation paramétrique atemporelle
            </span>
            <NJIconButton
              icon="close"
              label="Fermer"
              variant="tertiary"
              scale="sm"
              onClick={() => undefined}
            />
          </div>

          {/* ── Tabs ── */}
          <div style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            padding: '0px 24px',
            gap: '16px',
            flexShrink: 0,
            background: '#ffffff',
            boxSizing: 'border-box',
          }}>
            <NJTabs
              label="Étapes du formulaire"
              activeTab={activeTab}
              onClickTabItem={(id: string) => setActiveTab(id as TabId)}
            >
              {TABS.map((tab) => (
                <NJTab
                  key={tab.id}
                  id={tab.id}
                  label={isSmall ? tab.shortLabel : tab.label}
                  ariaControls={`panel-${tab.id}`}
                  isActive={activeTab === tab.id}
                />
              ))}
            </NJTabs>
          </div>

          {/* ── Tab panels ── */}
          <div className="opti-form-panels" style={{
            flex: 1,
            minHeight: 0,
            overflow: activeTab === 'temperatures-demand' ? 'hidden' : 'auto',
            display: 'flex',
            flexDirection: 'column',
            background: '#ffffff',
          }}>
            <NJTabPanel id="panel-general" isActive={activeTab === 'general'}>
              <GeneralTab
                nom={nom}                      onNomChange={setNom}
                description={description}      onDescriptionChange={setDescription}
                tempMin={tempMin}              onTempMinChange={setTempMin}
                tempMax={tempMax}              onTempMaxChange={setTempMax}
                nombreDePas={nombreDePas}      onNombreDePasChange={setNombreDePas}
                regulationMode={regulationMode} onRegulationModeChange={setRegulationMode}
                advancedOptions={advancedOptions}
                onToggleOption={toggleAdvancedOption}
                isActive={activeTab === 'general'}
              />
            </NJTabPanel>

            <NJTabPanel id="panel-temperatures-demand" isActive={activeTab === 'temperatures-demand'}>
              {visitedTabs.has('temperatures-demand') && <TempDemandTab tempMin={tempMin} tempMax={tempMax} nombreDePas={nombreDePas} onProductionValidityChange={setIsProductionDataValid} />}
            </NJTabPanel>

            <NJTabPanel id="panel-planning" isActive={activeTab === 'planning'}>
              {visitedTabs.has('planning') && <PlanningTab />}
            </NJTabPanel>

            <NJTabPanel id="panel-advanced" isActive={activeTab === 'advanced'}>
              {visitedTabs.has('advanced') && <AdvancedOptionsTab options={advancedOptions} onToggle={toggleAdvancedOption} />}
            </NJTabPanel>
          </div>

          {/* ── Footer ── */}
          <div className="opti-form-footer" style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            alignItems: 'center',
            padding: '16px 24px',
            gap: '16px',
            height: '56px',
            flexShrink: 0,
            borderTop: '1px solid #CCD4DA',
            background: '#ffffff',
            boxSizing: 'border-box',
          }}>
            <NJButton
              label="Annuler"
              variant="secondary"
              scale={isSmall ? 'sm' : 'md'}
              onClick={() => undefined}
            />
            <NJButton
              label={isSmall ? 'Préc.' : 'Précédent'}
              emphasis="subtle"
              scale={isSmall ? 'sm' : 'md'}
              disabled={isFirstTab}
              onClick={goToPrev}
            />
            <NJButton
              label="Suivant"
              emphasis="subtle"
              scale={isSmall ? 'sm' : 'md'}
              disabled={isLastTab}
              onClick={goToNext}
            />
            <NJButton
              label={isSmall ? 'Lancer' : "Lancer l'optimisation"}
              scale={isSmall ? 'sm' : 'md'}
              disabled={!nom.trim() || !isProductionDataValid}
              onClick={() => undefined}
            />
          </div>
        </div>
      </div>
    </PrototypeStudioLayout>
  );
}
